import React, { useMemo } from 'lib/react';
import PropTypes from 'prop-types';
import {
  EVButton,
  EVGrid,
  EVLink,
  EVPaper,
  EVSkeleton,
} from 'lib/components';
import { useDispatch, useSelector } from 'lib/redux';
import { useTranslation } from 'lib/translation';
import clsx from 'lib/clsx';
import TextInputField from 'commonComponents/textInputField';
import useMediaQuery from 'hooks/useMediaQuery';
import {
  updatePromoCode, placeOrder as placeOrderAction, getEstimatedCost, resetFinalizeOrderPage,
} from 'features/finalizeOrder/FinalizeOrder.actions';
import history from 'store/CreateHistory';
import { ORDER_ROUTE, ORDER_BLUEPRINT_ROUTE } from 'constants/RouteConstants';
import { LEGACY_PORTAL_LINKS, ESX_PRODUCT_NAME } from 'constants/AppConstants';
import Typography from 'commonComponents/typography';
import { isEntitledForHidePricingSelector } from 'utils/appSelectors.utils';
import OrderSummaryStyles from './OrderSummaryStyles';
import PlacingOrderLoader from '../PlacingOrderLoader.component';

function OrderSummary({ paymentSectionRef }) {
  const classes = OrderSummaryStyles();
  const {
    controlStates: { pricing, paymentInformation },
    estimatedCostResponse,
    placeOrderLoading,
    promoCode,
    upgradeFromReportId,
    upgradeESX,
    canAddEsxToCurrentProduct,
    fetchingEstimatedCost,
    isBillingAffiliateUser,
    bluePrintFileId,
    bluePrintId,
  } = useSelector(({
    finalizeOrder, placeOrder, userData: { controlStates, initParameters },
  }) => ({
    ...finalizeOrder,
    bluePrintId: placeOrder.bluePrintId,
    promoCode: placeOrder.promoCode,
    controlStates,
    isBillingAffiliateUser: initParameters?.isBillingAffiliate,
    bluePrintFileId: placeOrder.bluePrintFileId,
  }));
  const isEsxOnlyAddition = Boolean(upgradeESX && canAddEsxToCurrentProduct);
  const esxAddOn = (estimatedCostResponse?.orderProducts ?? [])
    .flatMap(({ addOns = [] }) => addOns)
    .find((addOn) => addOn?.name === ESX_PRODUCT_NAME);
  const isEntitledForHidePricing = useSelector(isEntitledForHidePricingSelector);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(640);

  const invalidPromoCodeHelperText = useMemo(() => {
    const discountError = estimatedCostResponse?.discounts?.find(({ isError }) => isError);
    if (discountError) return discountError.name;
    return '';
  }, [estimatedCostResponse]);

  const handlePromoCodeChange = ({ target: { value } }) => dispatch(updatePromoCode(value));

  const handleSubmit = async () => {
    const transientTokenData = await paymentSectionRef.current.tokenize();
    dispatch(placeOrderAction(transientTokenData));
  };

  const fetchEstimatedCost = () => (dispatch(getEstimatedCost()));

  const handleEditOrder = () => {
    dispatch(resetFinalizeOrderPage(true));
    history.unblock();
    history.push(bluePrintFileId ? ORDER_BLUEPRINT_ROUTE : ORDER_ROUTE);
  };

  const estimatedCostLoader = () => (
    <EVGrid className={clsx(classes.flexColumn, classes.terms)}>
      <EVGrid container style={{ gap: '10px', flexDirection: 'column' }}>
        <EVGrid container style={{ gap: '10px', alignItems: 'flex-start', alignSelf: 'stretch' }}>
          <EVSkeleton style={{ height: '24px', width: '240px', borderRadius: '6px' }} animation="wave" variant="rect" />
          <EVSkeleton style={{ height: '24px', flex: '1 0 0', borderRadius: '6px' }} animation="wave" variant="rect" />
        </EVGrid>
        <EVGrid container className={classes.orderDetailLoaderSummaryGrid}>
          <EVSkeleton style={{ height: '18px', width: '180px', borderRadius: '6px' }} animation="wave" variant="rect" />
          <EVSkeleton style={{ height: '18px', width: '82px', borderRadius: '6px' }} animation="wave" variant="rect" />
        </EVGrid>
      </EVGrid>
      <EVGrid container style={{ gap: '10px', flexDirection: 'column' }}>
        <EVSkeleton style={{ height: '18px', width: '136px', borderRadius: '6px' }} animation="wave" variant="rect" />
        <EVGrid container style={{ gap: '10px', alignItems: 'flex-start', alignSelf: 'stretch' }}>
          <EVSkeleton style={{ height: '44px', width: '172px', borderRadius: '6px' }} animation="wave" variant="rect" />
          <EVSkeleton style={{ height: '44px', width: '85px', borderRadius: '6px' }} animation="wave" variant="rect" />
        </EVGrid>
      </EVGrid>
      <EVGrid container style={{ gap: '72px', alignItems: 'flex-start', alignSelf: 'stretch' }}>
        <EVSkeleton style={{ height: '24px', flex: '1 0 0', borderRadius: '6px' }} animation="wave" variant="rect" />
        <EVSkeleton style={{ height: '18px', width: '82px', borderRadius: '6px' }} animation="wave" variant="rect" />
      </EVGrid>
      <EVGrid container style={{ gap: '72px', alignItems: 'flex-start', alignSelf: 'stretch' }}>
        <EVSkeleton style={{ height: '24px', flex: '1 0 0', borderRadius: '6px' }} animation="wave" variant="rect" />
        <EVSkeleton style={{ height: '18px', width: '82px', borderRadius: '6px' }} animation="wave" variant="rect" />
      </EVGrid>
      <EVGrid container className={classes.orderDetailLoaderSummaryGridA}>
        <EVSkeleton style={{ height: '24px', flex: '1 0 0', borderRadius: '6px' }} animation="wave" variant="rect" />
        <EVSkeleton style={{ height: '18px', width: '82px', borderRadius: '6px' }} animation="wave" variant="rect" />
      </EVGrid>
    </EVGrid>

  );
  return (
    <EVPaper classes={{ root: classes.orderSummary }} square={isMobile}>
      {placeOrderLoading ? <PlacingOrderLoader /> : null}
      <Typography variant="h3-600" fontColor="ocean-700">{t('finalize_order__order_summary')}</Typography>
      {fetchingEstimatedCost ? estimatedCostLoader()
        : estimatedCostResponse && (
        <EVGrid container className={classes.selectedProductsDetailsList}>
          {
          isEsxOnlyAddition ? (
            esxAddOn && (() => {
              const showCost = !pricing?.hidden && !isEntitledForHidePricing;
              return (
                <div className={classes.orderSummaryRows} key={esxAddOn.name}>
                  <EVGrid container justifyContent="space-between" className="line1">
                    <Typography>{esxAddOn.name}</Typography>
                    <Typography>{showCost && esxAddOn.cost}</Typography>
                  </EVGrid>
                </div>
              );
            })()
          ) : (
            (estimatedCostResponse.orderProducts ?? []).map(({
              product: { name: productName, cost: productCost },
              delivery: { name: deliveryName, cost: deliveryCost },
              addOns = [],
            }) => {
              // Show "Instant Delivery" for Property Data products, keep original delivery name for others
              const displayDeliveryName = productName === 'Property Data' ? 'Instant Delivery' : deliveryName;
              const showCost = !pricing?.hidden && !isEntitledForHidePricing;
              return (
                <div className={classes.orderSummaryRows} key={productName}>
                  <EVGrid container justifyContent="space-between" className="line1">
                    <Typography>{productName}</Typography>
                    <Typography>{showCost && productCost}</Typography>
                  </EVGrid>
                  {addOns.length > 0 && (
                    <EVGrid>
                      <Typography variant="label-600" fontColor="charcoal-800">{t('finalize_order__add_ons')}</Typography>
                      {addOns.map(({ name: addOnName, cost: addOnCost }) => (
                        <EVGrid container justifyContent="space-between" className="line1" key={addOnName}>
                          <Typography>{addOnName}</Typography>
                          <Typography>{showCost && addOnCost}</Typography>
                        </EVGrid>
                      ))}
                    </EVGrid>
                  )}
                  <EVGrid container justifyContent="space-between" className="line2">
                    <Typography>{displayDeliveryName}</Typography>
                    <Typography>{showCost && deliveryCost}</Typography>
                  </EVGrid>
                </div>
              );
            })
          )
}
          {(bluePrintId !== '') && (
          <div className={classes.orderSummaryRows}>
            <EVGrid container justifyContent="space-between" className="line1">
              <Typography>{t('blueprint_order__blueprint_id')}</Typography>
            </EVGrid>
            <EVGrid container justifyContent="space-between" className="line2">
              <Typography>{`# ${bluePrintId}`}</Typography>
            </EVGrid>
          </div>
          )}
          {estimatedCostResponse.numBuildings > 1 && (
          <div className={classes.orderSummaryRows}>
            <EVGrid container justifyContent="space-between" className="line1">
              <Typography>{t('finalize_order__order_summary__no_of_buildings')}</Typography>
              <Typography>{estimatedCostResponse.numBuildings}</Typography>
            </EVGrid>
          </div>
          )}
        </EVGrid>
        )}
      {!paymentInformation?.hidden && (
        <EVGrid id="finalize-order-promo-code" container wrap="nowrap" alignItems="flex-end" style={{ gap: '8px' }}>
          <EVGrid>
            <TextInputField
              inputId="promoCode"
              inputValue={promoCode}
              handleChange={handlePromoCodeChange}
              inputLabelProps={{ className: classes.promoCodeLabel }}
              inputFieldProps={{
                fullWidth: false, classes: { root: classes.promoCodeInput }, FormHelperTextProps: { classes: { error: classes.promoInputHelperText } },
              }}
              labelText={t('finalize_order__order_summary__have_a_promo_code')}
              helperText={invalidPromoCodeHelperText}
            />
          </EVGrid>
          <EVButton id="applyBtn" variant="contained" className={clsx(classes.button, classes.applyBtn)} onClick={fetchEstimatedCost}>
            {t('finalize_order__order_summary__apply_promo_code')}
          </EVButton>
        </EVGrid>

      )}

      {estimatedCostResponse && (
      <>
        {!pricing?.hidden && !isBillingAffiliateUser && !isEntitledForHidePricing && (
          <>
            {/* #genai: Wrapper kept in Scenario A (renders empty) so the &:empty padding rule preserves the 24px spacer before the total. */}
            <EVGrid className={clsx(classes.orderSummaryRows, classes.flexColumn)}>
              {
            !isEsxOnlyAddition && estimatedCostResponse.discounts?.map(({ name, amount, isError }) => !isError && (
            <EVGrid container justifyContent="space-between" className="line1" key={name}>
              <EVGrid item xs={6} sm={6} md={6} lg={6}>
                <Typography>{name}</Typography>
              </EVGrid>
              <Typography>
                -
                {' '}
                {amount}
              </Typography>
            </EVGrid>
            ))
          }
              {
          !isEsxOnlyAddition && estimatedCostResponse.includesTax
          && (
            <EVGrid className={clsx(classes.orderSummaryRows, classes.flexColumn, classes.tax)}>
              {[estimatedCostResponse.subtotal, estimatedCostResponse.tax].map(({ name, cost }) => (
                <EVGrid container justifyContent="space-between" className="line1" key={name}>
                  <EVGrid item xs={6} sm={6} md={6} lg={6}>
                    <Typography>{name}</Typography>
                  </EVGrid>
                  <Typography>{cost}</Typography>
                </EVGrid>
              ))}
            </EVGrid>
          )
        }
            </EVGrid>

            <EVGrid container justifyContent="space-between" className={classes.estimatedTotal} wrap="nowrap">
              <EVGrid item xs={6} sm={6} md={6} lg={6}>
                <Typography>{estimatedCostResponse?.total?.name ?? t('EstimatedTotal')}</Typography>
              </EVGrid>
              <Typography>
                {/* #genai: total.cost includes the source product for ESX-only; bill only the ESX add-on. */}
                {isEsxOnlyAddition ? esxAddOn?.cost : estimatedCostResponse?.total?.cost}
              </Typography>
            </EVGrid>
          </>
        )}
        <EVGrid className={clsx(classes.flexColumn, classes.terms)}>
          {/* TODO Based on discussion with kyle Remove or add Payment message
          <Typography>{t('finalize_order__payment_information__master_billing')}</Typography> */}
          <Typography>
            {t('finalize_order__order_summary__payment_terms_of_use_1')}
            <EVLink target="_blank" href={LEGACY_PORTAL_LINKS.termsOfUse}>{t('finalize_order__order_summary__payment_terms_of_use_2')}</EVLink>
          </Typography>
        </EVGrid>
        <EVGrid container justifyContent={upgradeFromReportId ? 'flex-end' : 'space-between'} className={classes.buttonContainer}>
          {!upgradeFromReportId && (
          <EVButton id="edit-order-btn" variant="outlined" color="primary" className={classes.button} onClick={handleEditOrder}>
            {t('finalize_order__order_summary__payment_edit_order')}
          </EVButton>
          )}
          <EVButton id="submitOrderBtn" onClick={handleSubmit} variant="contained" color="primary" className={classes.button}>
            {t('finalize_order__order_summary__payment_submit_order')}
          </EVButton>
        </EVGrid>
      </>
      )}
    </EVPaper>
  );
}

OrderSummary.propTypes = {
  paymentSectionRef: PropTypes.shape({
    current: PropTypes.shape({
      tokenize: PropTypes.func,
    }),
  }),
};

OrderSummary.defaultProps = {
  paymentSectionRef: null,
};

export default OrderSummary;
